<template>
  <main class="dashboard-main">
    <SearchFilters
      :initial-search-term="searchTerm"
      :initial-filter="selectedFilter"
      :is-fleet-user="isFleetUser"
      :fleet-list="fleetList"
      @search="handleSearch"
      @filter-change="handleFilterChange"
      @category-search="handleCategorySearch"
    />
    
    <DataTable
      :loading="loading"
      :rows="tableData"
      :total-count="totalCount"
      :columns="tableColumns"
      @search="handleTableSearch"
      @row-click="handleRowClick"
      @action="handleAction"
      @finished="handleTableFinished"
    />
  </main>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import SearchFilters from './MainSearchFilterComponent.vue';
import DataTable from './MainDataTableComponent.vue';
import { FLEET_LIST, FILTER_OPTIONS, FLEET_HIDDEN_COLUMNS } from '../businessLogic/MainConstants';

export default {
  name: 'MainComponent',
  components: {
    SearchFilters,
    DataTable
  },
  
  setup() {
    const store = useStore();
    const router = useRouter()

    // Local state
    const openParenCount = ref(0);  // New: Track Shift+9 presses for "((("
    const showDebugColumn = ref(false);  // New: Flag to toggle 'DEBUG' column
    const starCount = ref(0)

    const loading = ref(false)
    const searchTerm = ref('')
    const selectedFilter = ref(1)
    
    // Computed properties
    const isFleetUser = computed(() => 
      store.state.auth.user?.roles?.includes('FLEET') || false
    )
    
    const tableData = computed(() => store.state.dash.db)
    const totalCount = computed(() => store.state.dash.totalRecordCount)    
    // Table columns configuration
    const tableColumns = computed(() => {
      const allColumns = [
        { label: "ID",    field: "id",    width: "1%", sortable: true, isKey: true },
        { label: "Fleet", field: "fleet", width: "4%", sortable: true },
        { label: "RZ",    field: "spz",   width: "2%", sortable: true },
        { label: "Číslo zakázky MyWay",   field: "mywayID",     width: "2%", sortable: true },
        { label: "Číslo zakazky dealera", field: "dealerDocID", width: "2%", sortable: true },
        { label: "Shoda ceny",     field: "shodaCeny",     width: "0%", sortable: false },
        { label: "Nahráno do MW",  field: "statusNahrani", width: "0%", sortable: false },
        { label: "Zpráva z MyWay", field: "mywayZprava",   width: "20%", sortable: true },
        { label: "Důvod ukončení nahrání", field: "duvodUkonceni", width: "20%", sortable: true },
        { label: "Stav zakázky", field: "stav_zakazky", width: "5%", sortable: true },
        { label: "Akce",         field: "akce",         width: "5%", sortable: false },
        { label: "Img",          field: "imgLink",      width: "3%", sortable: false }
      ]
      console.log('### isFleetUser in allColumns', isFleetUser.value);
      // Filter out hidden columns for FLEET users
      if (isFleetUser.value) {
        return allColumns.filter(column => !FLEET_HIDDEN_COLUMNS.includes(column.field))
      }
      if (showDebugColumn.value) { // New column at start
        return [{ label: 'DEBUG', field: 'debug', width: '5%', sortable: false }, ...allColumns];
      }
      return allColumns;
    })


    // Methods
    const fetchData = async () => {
      try {
        loading.value = true
        await store.dispatch('dash/fetchData')
      } catch (error) {
        console.error('Failed to fetch data:', error)
      } finally {
        loading.value = false
      }
    }
    
    const handleSearch = async (searchData) => {
      searchTerm.value = searchData.term
      selectedFilter.value = searchData.filter
      await store.commit('dash/setSearchOTHER', searchData.term)
      await store.commit('dash/setFilter', searchData.filter)
      await fetchData();
    }
    
    const handleFilterChange = async (filterValue) => {
      selectedFilter.value = filterValue
      await store.commit('dash/setFilter', filterValue)
      await fetchData()
    }
    
    const handleCategorySearch = async (categoryData) => {
      try {
        console.log('Category search data:', categoryData);
        
        await store.dispatch('dash/runAction', {
          action: 'categoryCheck',
          dashID: 0,
          payload: {
            spz: categoryData.spz,
            flotila: categoryData.flotila
          }
        });
        setTimeout(function(){ fetchData();},2000);
      } catch (error) {
        console.error('Category search failed:', error)
        // Handle error (show toast notification, etc.)
      }
    }
    
    const handleTableSearch = async (offset, limit, order, sort) => {
      await store.commit('dash/setTabControl', { offset, limit, order, sort })
      await fetchData()
    }
    
    const handleRowClick = (row) => {
      store.commit('dash/setDashID', row.id)
    }
    
    const handleAction = async (actionData) => {
      if (['Nelze', '---', '...', 'Stale aktivni'].includes(actionData.action)) {
        return
      }
      
      try {
        await store.dispatch('dash/runAction', {
          action: actionData.action,
          dashID: actionData.id,
          payload: actionData.payload
        })
        
        // Refresh data after action
        setTimeout(fetchData, 1000)
      } catch (error) {
        console.error('Action failed:', error)
      }
    }
    
    const handleTableFinished = () => {
      loading.value = false
    }
    
    // Easter egg functionality
    const handleKeyDown = (event) => {
      const isInputOrTextarea = event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA';
      if (!isInputOrTextarea &&
          (event.key === '*' || event.key === 'NumpadMultiply' || event.keyCode === 106)) {
        starCount.value++
        if (starCount.value === 4) {
          starCount.value = 0
          router.push({name: 'appWrkLogs'});
          // store.dispatch('dash/fetchLogs')
          // Navigate to logs page
        }
      } else {
        starCount.value = 0
      }

      if (!isInputOrTextarea && (event.shiftKey && event.key === '(')) {
        openParenCount.value++;
        if (openParenCount.value === 4) {
          openParenCount.value = 0;
          showDebugColumn.value = !showDebugColumn.value;
        }
      } else {
        openParenCount.value = 0;
      }
    }
    
    // Lifecycle
    onMounted(async () => {
      document.addEventListener('keydown', handleKeyDown)
      
      if (isFleetUser.value) {
        handleFilterChange(FILTER_OPTIONS.ALL);
        //selectedFilter.value = FILTER_OPTIONS.ALL
      }
      
      await fetchData()
    })
    
    onBeforeUnmount(() => {
      document.removeEventListener('keydown', handleKeyDown)
    })
    
    return {
      // State
      loading,
      searchTerm,
      selectedFilter,
      starCount, openParenCount, showDebugColumn,
      
      // Computed
      isFleetUser,
      tableData,
      totalCount,
      tableColumns,
      fleetList: FLEET_LIST,
      
      // Methods
      handleSearch,
      handleFilterChange,
      handleCategorySearch,
      handleTableSearch,
      handleRowClick,
      handleAction,
      handleTableFinished
    }
  }
}
</script>

<style scoped>
/* ::v-deep is way of pushing css into components */

main {
    width: 100%;
    margin-top: 20px;
}
navigation {
}

section {
    display: flex;
    justify-content:flex-start;
    padding: 10px;
    border: 1px solid #444;
    background-color: #333;
    border-radius: 5px;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
}

section[name="search"] {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
}

.headText{
    position: absolute;
    margin-top: -29px
}

.search_input, .categoryCheck {
    display: flex;
    flex-direction: row;
    align-items: center;
}

.search_input > input,
.filter {
    border-radius: 5px;
    border: 1px solid #555;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
    margin: 0px 6px 0px 0px;
    padding: 7px;
    color: #fff;
    background-color: #444;
}
.search_spz  { width: 100px; }
.search_other{ width: 250px; }

.search_input > input:hover { border-color: #8bc3ff; }

.search_input > button {
    border-radius: 5px;
    color: white;
    transition: background-color 0.3s;
}
.search_btn {
    font-size: medium;
    padding: 6px 20px;
    border: none;
    border-radius: 0px;
    background-color: #007bff;
    color: #fff;
    cursor: pointer;
    transition: background-color 0.3s;
}
.search_btn:hover {
    background-color: #0056b3;
}

.filter {
    width: 210px;
    border: 1px solid #555;
    background-color: #444;
    color: white;
    height: fit-content;
    margin-left: 30px;
/*    font-size: 12px;*/
}

select.filter:focus {
    outline: none;
    border-color: #8bc3ff;
}

select.filter:hover {
    border-color: #8bc3ff;
}

#remove_status ul {
    display: flex;
    list-style-type: none;
    padding: 0;
    margin: 0;
}
#remove_status  p {
    display: block;
    color: #ffcc00;
    font-size: 13px;
    margin-right: 10px;
    margin-block: 0px;
}

#remove_status li {
    display: flex;
    align-items: center;
    padding: 10px 20px;
    text-decoration: none;
    font-size: 12px;
    transition: 0.3s;
}

#remove_status li:hover {background-color: #333; }


</style>
